import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  LinearProgress,
  Stack,
  TextField,
  MenuItem,
  Typography,
  IconButton,
} from "@mui/material";
import { Box } from "@mui/system";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

function EditScienceDialog({
  open,
  onClose,
  saving,
  editTarget,
  editValues,
  setEditValues,
  videoUploading,
  videoProgress,
  onSave,
  onCopy,
}) {
  return (
    <Dialog
      open={open}
      onClose={() => (saving ? null : onClose)}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Edit Science Video</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Title"
            value={editValues.title}
            onChange={(e) =>
              setEditValues((v) => ({ ...v, title: e.target.value }))
            }
            fullWidth
          />
          <TextField
            select
            label="Category"
            value={editValues.category}
            onChange={(e) =>
              setEditValues((v) => ({ ...v, category: e.target.value }))
            }
            fullWidth
          >
            <MenuItem value="sciences">sciences</MenuItem>
            <MenuItem value="meditation">meditation</MenuItem>
            <MenuItem value="reflection">reflection</MenuItem>
          </TextField>
          <Box
            sx={{
              border: "1px dashed #cbd5e1",
              borderRadius: 2,
              p: 1.5,
              background: "#f8fafc",
            }}
          >
            <Typography variant="overline" sx={{ color: "#64748b" }}>
              Video file
            </Typography>
            <Typography variant="caption" sx={{ color: "#64748b", mb: 0.5 }}>
              Replace video file (optional)
            </Typography>
            {editTarget?.url ? (
              <Box
                sx={{
                  border: "1px solid #e2e8f0",
                  borderRadius: 2,
                  p: 1,
                  mb: 0.5,
                  background: "#fff",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: "#64748b", minWidth: 92 }}
                >
                  Current video
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#0f172a",
                    flex: 1,
                    minWidth: 0,
                  }}
                  noWrap
                  title={editTarget.url}
                >
                  {editTarget.url}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => onCopy(editTarget.url)}
                  title="Copy link"
                >
                  <ContentCopyIcon fontSize="inherit" />
                </IconButton>
              </Box>
            ) : null}
            <Box
              sx={{
                border: "1px dashed #cbd5e1",
                borderRadius: 2,
                p: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1,
                background: "#f8fafc",
              }}
            >
              <Button variant="outlined" component="label">
                Choose file
                <input
                  type="file"
                  accept="video/*"
                  hidden
                  onChange={(e) =>
                    setEditValues((v) => ({
                      ...v,
                      videoFile: e.target.files?.[0] || null,
                    }))
                  }
                />
              </Button>
              <Typography variant="body2" sx={{ color: "#475569" }}>
                {editValues.videoFile
                  ? editValues.videoFile.name
                  : "No file selected"}
              </Typography>
            </Box>
          </Box>
          <Button
            variant="text"
            color="error"
            onClick={() =>
              setEditValues((v) => ({
                ...v,
                removeVideo: true,
                videoFile: null,
              }))
            }
          >
            Remove current video
          </Button>
          <Box
            sx={{
              border: "1px dashed #cbd5e1",
              borderRadius: 2,
              p: 1.5,
              background: "#f8fafc",
            }}
          >
            <Typography variant="overline" sx={{ color: "#64748b" }}>
              Workbook
            </Typography>
            <Typography variant="caption" sx={{ color: "#64748b", mb: 0.5 }}>
              Replace workbook PDF (optional)
            </Typography>
            {editTarget?.workbookUrl ? (
              <Box
                sx={{
                  border: "1px solid #e2e8f0",
                  borderRadius: 2,
                  p: 1,
                  mb: 0.5,
                  background: "#fff",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: "#64748b", minWidth: 110 }}
                >
                  Current workbook
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#0f172a",
                    flex: 1,
                    minWidth: 0,
                  }}
                  noWrap
                  title={editTarget.workbookUrl}
                >
                  {editTarget.workbookUrl}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => onCopy(editTarget.workbookUrl)}
                  title="Copy link"
                >
                  <ContentCopyIcon fontSize="inherit" />
                </IconButton>
              </Box>
            ) : null}
            <Box
              sx={{
                border: "1px dashed #cbd5e1",
                borderRadius: 2,
                p: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1,
                background: "#f8fafc",
              }}
            >
              <Button variant="outlined" component="label">
                Choose PDF
                <input
                  type="file"
                  accept="application/pdf"
                  hidden
                  onChange={(e) =>
                    setEditValues((v) => ({
                      ...v,
                      workbookFile: e.target.files?.[0] || null,
                      removeWorkbook: false,
                    }))
                  }
                />
              </Button>
              <Typography variant="body2" sx={{ color: "#475569" }}>
                {editValues.workbookFile
                  ? editValues.workbookFile.name
                  : "No file selected"}
              </Typography>
            </Box>
          </Box>
          <Button
            variant="text"
            color="error"
            onClick={() =>
              setEditValues((v) => ({
                ...v,
                workbookFile: null,
                removeWorkbook: true,
              }))
            }
          >
            Remove workbook
          </Button>
          {videoUploading && (
            <Box sx={{ mt: 1 }}>
              <LinearProgress variant="determinate" value={videoProgress} />
              <Typography variant="caption" sx={{ color: "#64748b" }}>
                {Math.round(videoProgress)}%
              </Typography>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" disabled={saving} onClick={onSave}>
          {saving ? (
            <CircularProgress size={18} sx={{ color: "#fff" }} />
          ) : (
            "Save"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default EditScienceDialog;
